/**
 * Product Image Uploader
 * Sprint 4I: Full-featured image management for products
 * 
 * Features:
 * - Drag & drop
 * - Multiple file selection
 * - Progress indicators
 * - Set primary image
 * - Gallery management (max 5)
 * - Delete images
 * - Responsive design
 */

import { useState, useRef, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, CheckCircle2 } from 'lucide-react';
import { mediaApi } from '@/lib/api';
import { ProductImage } from '@/lib/constants';
import { logger } from '@/lib/logger';
import { compressImage, formatFileSize } from '@/lib/imageCompressor';
// ProgressiveImage import removed - not currently used

interface ProductImageUploaderProps {
  productId: string;
  primaryImage: ProductImage | null;
  galleryImages: ProductImage[];
  onUpdate: (primaryImageId: string | null, galleryImageIds: string[]) => void;
  maxImages?: number;
}

interface UploadProgress {
  file: File;
  progress: number;
  mediaId?: string;
  error?: string;
}

export function ProductImageUploader({
  productId,
  primaryImage,
  galleryImages,
  onUpdate,
  maxImages = 5,
}: ProductImageUploaderProps) {
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const totalImages = (primaryImage ? 1 : 0) + galleryImages.length;
  const canUploadMore = totalImages + uploads.length < maxImages;
  
  // Validate file
  const validateFile = (file: File): string | null => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return 'Invalid file type. Please use JPG, PNG, or WebP.';
    }
    // Allow files up to 20MB - we'll compress them if needed
    if (file.size > 20 * 1024 * 1024) {
      return 'File too large. Maximum size is 20MB.';
    }
    return null;
  };
  
  // Handle file upload
  const uploadFile = async (file: File, setPrimary: boolean = false) => {
    // uploadId can be used for tracking - currently not needed
    
    setUploads((prev) => [...prev, { file, progress: 0 }]);
    
    try {
      // Compress image if needed
      const { file: compressedFile, wasCompressed, originalSize, compressedSize } = await compressImage(file);
      
      if (wasCompressed) {
        logger.info('Image compressed', {
          original: formatFileSize(originalSize),
          compressed: formatFileSize(compressedSize),
          reduction: `${Math.round((1 - compressedSize / originalSize) * 100)}%`,
        });
      }
      
      // Upload file
      const result = await mediaApi.upload(compressedFile, productId, setPrimary);
      
      setUploads((prev) =>
        prev.map((u) =>
          u.file === file ? { ...u, progress: 100, mediaId: result.id } : u
        )
      );
      
      // Update parent component with the new image data
      // This triggers an immediate update in the product store
      if (setPrimary) {
        await onUpdate(result.id, galleryImages.map((img) => img.id));
      } else {
        const newGalleryIds = [...galleryImages.map((img) => img.id), result.id];
        await onUpdate(primaryImage?.id || null, newGalleryIds);
      }
      
      logger.info('Image uploaded successfully', { mediaId: result.id });
      
      // Remove from uploads after 2s
      setTimeout(() => {
        setUploads((prev) => prev.filter((u) => u.file !== file));
      }, 2000);
    } catch (error: any) {
      logger.error('Failed to upload image', error);
      setUploads((prev) =>
        prev.map((u) =>
          u.file === file ? { ...u, error: error.message || 'Upload failed' } : u
        )
      );
    }
  };
  
  // Handle multiple files
  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    
    for (const file of fileArray) {
      if (!canUploadMore && validFiles.length >= maxImages - totalImages) {
        alert(`Maximum ${maxImages} images allowed`);
        break;
      }
      
      const error = validateFile(file);
      if (error) {
        alert(`${file.name}: ${error}`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    // Upload files (max 2 concurrent)
    validFiles.forEach((file, index) => {
      setTimeout(() => {
        const setPrimary = totalImages === 0 && index === 0;
        uploadFile(file, setPrimary);
      }, index * 100);
    });
  }, [canUploadMore, maxImages, totalImages]);
  
  // Drag & drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };
  
  // Click to upload
  const handleClick = () => {
    if (canUploadMore) {
      fileInputRef.current?.click();
    } else {
      alert(`Maximum ${maxImages} images allowed`);
    }
  };
  
  // Set as primary
  const handleSetPrimary = async (imageId: string) => {
    try {
      const newGalleryIds = primaryImage
        ? [primaryImage.id, ...galleryImages.map((img) => img.id).filter((id) => id !== imageId)]
        : galleryImages.map((img) => img.id).filter((id) => id !== imageId);
      
      onUpdate(imageId, newGalleryIds);
      logger.info('Primary image updated', { imageId });
    } catch (error: any) {
      logger.error('Failed to set primary image', error);
      alert('Failed to set primary image');
    }
  };
  
  // Delete image
  const handleDelete = async (imageId: string) => {
    if (!confirm('Delete this image?')) return;
    
    try {
      await mediaApi.delete(imageId);
      
      if (primaryImage?.id === imageId) {
        // Deleted primary - promote first gallery image
        const newPrimary = galleryImages[0]?.id || null;
        const newGallery = galleryImages.slice(1).map((img) => img.id);
        onUpdate(newPrimary, newGallery);
      } else {
        // Deleted gallery image
        const newGallery = galleryImages
          .map((img) => img.id)
          .filter((id) => id !== imageId);
        onUpdate(primaryImage?.id || null, newGallery);
      }
      
      logger.info('Image deleted', { imageId });
    } catch (error: any) {
      logger.error('Failed to delete image', error);
      alert('Failed to delete image');
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-all
          ${isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'border-gray-300 dark:border-gray-700'}
          ${!canUploadMore ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-blue-400'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="text-lg font-medium mb-2">
          {canUploadMore ? 'Drop images here or click to upload' : `Maximum ${maxImages} images`}
        </p>
        <p className="text-sm text-gray-500">
          JPG, PNG, or WebP • Max 5MB • {totalImages}/{maxImages} images
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          disabled={!canUploadMore}
        />
      </div>
      
      {/* Upload Progress */}
      {uploads.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-medium">Uploading...</h3>
          {uploads.map((upload, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded">
              <div className="flex-1">
                <p className="text-sm font-medium truncate">{upload.file.name}</p>
                {upload.error ? (
                  <p className="text-xs text-red-500">{upload.error}</p>
                ) : (
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
                    <div
                      className="bg-blue-500 h-1.5 rounded-full transition-all"
                      style={{ width: `${upload.progress}%` }}
                    />
                  </div>
                )}
              </div>
              {upload.progress === 100 && <CheckCircle2 className="w-5 h-5 text-green-500" />}
            </div>
          ))}
        </div>
      )}
      
      {/* Image Gallery */}
      <div className="space-y-2">
        <h3 className="font-medium">Product Images</h3>
        
        {totalImages === 0 && uploads.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>No images yet. Upload some to get started!</p>
          </div>
        )}
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {/* Primary Image */}
          {primaryImage && (
            <div className="relative group">
              <img
                src={primaryImage.mdUrl}
                alt="Primary product image"
                className="w-full aspect-square object-cover rounded-lg border-2 border-blue-500"
              />
              <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                Primary
              </div>
              <button
                onClick={() => handleDelete(primaryImage.id)}
                className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete image"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* Gallery Images */}
          {galleryImages.map((image) => (
            <div key={image.id} className="relative group">
              <img
                src={image.mdUrl}
                alt="Product image"
                className="w-full aspect-square object-cover rounded-lg border border-gray-300 dark:border-gray-700"
              />
              <button
                onClick={() => handleSetPrimary(image.id)}
                className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                title="Set as primary"
              >
                Set Primary
              </button>
              <button
                onClick={() => handleDelete(image.id)}
                className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                title="Delete image"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

